import { Component, Inject, OnInit, PLATFORM_ID, afterNextRender, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../_services/api/auth.service';
import { FormService } from '../../_services/app/form.service';
import { Address } from '../../_models/address.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../../_models/api-response.model';
import { RegisterRequest } from '../../_models/auth/register_request.model';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  private authService = inject(AuthService);
  private formService = inject(FormService);
  private router = inject(Router);

  registerForm!: FormGroup | null;
  currentStep: number = 1;
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  step1Fields = ['firstName', 'lastName', 'email', 'phoneNumber', 'company', 'vatNumber'];

  constructor(@Inject(PLATFORM_ID) private platformId: Object){
    afterNextRender(() => {
      this.initializeForm();
    });
  }

  ngOnInit(): void {}

  private initializeForm(): void {
    this.registerForm = this.formService.createRegisterForm();
  }

  get f(): { [key: string]: FormControl } {
    if (!this.registerForm || !this.registerForm.controls) {
      return {};
    }
    return this.registerForm.controls as { [key: string]: FormControl };
  }

  get address(): { [key: string]: FormControl } {
    const addressGroup = this.registerForm?.get('address');
    if (!addressGroup || !(addressGroup instanceof FormGroup) || !addressGroup.controls) {
      return {};
    }
    return addressGroup.controls as { [key: string]: FormControl };
  }

  goToNextStep(): void {
    if (!this.registerForm) return;
    
    this.markStep1FieldsTouched();
    
    if (this.isStep1Valid()) {
      this.currentStep = 2;
      this.errorMessage = '';
      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } else {
      this.errorMessage = 'Veuillez corriger les erreurs avant de continuer.';
    }
  }

  goToPreviousStep(): void {
    this.currentStep = 1;
    this.errorMessage = '';
  }

  private markStep1FieldsTouched(): void {
    if (!this.registerForm) return;
    
    this.step1Fields.forEach(field => {
      if (!this.registerForm) return;
      const control = this.registerForm.get(field);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  isStep1Valid(): boolean {
    if (!this.registerForm) return false;
    
    let isValid = true;
    
    this.step1Fields.forEach(field => {
      if (!this.registerForm) return;
      const control = this.registerForm.get(field);
      if (control && control.invalid) {
        isValid = false;
      }
    });
    
    return isValid;
  }

  onSubmit(): void {
    if (!this.registerForm) return;
    
    this.formService.markFormGroupTouched(this.registerForm);
    
    if (this.registerForm.invalid) {
      this.errorMessage = 'Veuillez corriger les erreurs dans le formulaire.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.registerForm.value;
    const registerRequest: RegisterRequest = {
      email: formValue.email,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phoneNumber,
      company: formValue.company,
      vatNumber: formValue.vatNumber,
      password: formValue.password,
      address: formValue.address.street ? formValue.address as Address : undefined
    };

    this.authService.register(registerRequest).subscribe({
      next: (response: ApiResponse<void>) => {
        this.isLoading = false;
        this.successMessage = response.message || 'Inscription réussie! Veuillez vérifier votre email pour activer votre compte.';
        if (!this.registerForm) return; this.registerForm.reset();
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        
        if (error.status === 400) {
          this.errorMessage = error.error?.message || 'Erreur de validation. Veuillez vérifier vos informations.';
        } else if (error.status === 409) {
          this.errorMessage = 'Un utilisateur avec cet email ou nom d\'utilisateur existe déjà.';
        } else {
          this.errorMessage = 'Une erreur est survenue lors de l\'inscription. Veuillez réessayer.';
        }
      }
    });
  }

  togglePassword(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  getFieldError(fieldName: string): string {
    const control = this.registerForm?.get(fieldName);
    if (control && control.errors && control.touched) {
      const errors = control.errors;
      if (errors['required']) return 'Ce champ est obligatoire';
      if (errors['email']) return errors['email'];
      if (errors['password']) return errors['password'];
      if (errors['phone']) return errors['phone'];
      if (errors['maxlength']) return `Longueur maximale dépassée`;
      if (errors['mismatch']) return 'Les mots de passe ne correspondent pas.';
    }
    return '';
  }

  hasError(fieldName: string): boolean {
    const control = this.registerForm?.get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  resendVerification(): void {
    const email = this.registerForm?.get('email')?.value;
    if (email) {
      this.authService.resendVerification(email).subscribe({
        next: (response) => {
          this.successMessage = 'Lien de vérification renvoyé avec succès! Vérifiez votre boîte email.';
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = 'Erreur lors de l\'envoi du lien de vérification.';
        }
      });
    } else {
      this.errorMessage = 'Impossible de renvoyer le lien. Veuillez vous inscrire à nouveau.';
    }
  }
}